/**
 * Inspection Create Wizard - beta foundation.
 *
 * Three steps:
 *   1. Sub-type selection (cards driven by /api/inspections/subtypes/)
 *   2. Linkage + scheduling (claim/holding UUID required per sub-type flag,
 *      scheduled_for, inspector_uuid)
 *   3. Review + submit → POST /api/inspections/ → redirect to detail
 *
 * Lands the new inspection in `draft` lifecycle status. Once the
 * workflow seed is wired, the detail page will surface `schedule` /
 * `start_inspection` actions; for now the wizard sets `scheduled_for`
 * directly and the inspection sits in draft.
 */

import { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router'
import {
  ClipboardCheck,
  CalendarCheck,
  Home,
  Pencil,
  Shuffle,
  ShieldCheck,
  Ruler,
  Search,
  ArrowLeft,
  ArrowRight,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { AppShell } from '@/admin/components/app-shell'
import { FormStepper } from '@/admin/components/form-stepper'
import { DetailCard } from '@/admin/components/shared/detail-card'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { PhoneInput } from '@/shared/components/ui/phone-input'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useInspectionSubTypes,
  useCreateInspection,
  useInspectorSearch,
  useHoldingSearch,
  useClaimSearch,
  useContractorQuotas,
  holdingConsumerName,
  holdingLocation,
  type InspectionSubType,
} from '@/admin/hooks/use-inspections'
import type { ClaimRow } from '@/admin/hooks/use-claims'
import { formatHoldingId } from '@/admin/lib/formatting'
import { useOrg } from '@/admin/hooks/use-org'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { usePartnershipSearch } from '@/admin/hooks/use-products'
import type { ResolvedAddress } from '@/admin/hooks/use-lookups'
import { AddressCapture } from '@/admin/components/registration-wizard/address-capture'
import { SearchablePicker } from './searchable-picker'
import { TimeSelect } from '@/shared/components/ui/time-select'

type Step = 'sub-type' | 'context' | 'review'

// UUIDs in this wizard are paste-only for beta - picker UIs land
// in a follow-up session. Anything that doesn't match the v4 / v8
// UUID shape gets stripped to null on submit so the backend doesn't
// reject the whole request because a tester typed "Mama Mia" into
// a field labelled "Contractor UUID". A toast surfaces what was
// stripped so the user knows what happened.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function uuidOrNull(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (UUID_RE.test(trimmed)) return trimmed
  return null
}

const ICONS: Record<string, typeof ClipboardCheck> = {
  'clipboard-check': ClipboardCheck,
  'calendar-check': CalendarCheck,
  shuffle: Shuffle,
  'shield-check': ShieldCheck,
  ruler: Ruler,
  search: Search,
}

interface WizardState {
  sub_type_key: string
  claim_uuid: string
  /** Display label + secondary line for the picked claim. */
  claim_label: string
  claim_secondary: string
  holding_uuid: string
  holding_label: string
  /** Secondary context line for the picked holding (consumer · address). */
  holding_secondary: string
  /** Property address for the picked holding (read-only summary). */
  holding_address: string
  warranty_uuid: string
  contractor_uuid: string
  contractor_label: string
  /** Secondary context for picked contractor (role · membership #). */
  contractor_secondary: string
  /**
   * True when the contractor was auto-derived from the holding's installing
   * dealer rather than picked by hand. An auto (or still-empty) contractor is
   * re-derived when the holding changes; an explicit pick is never overruled.
   */
  contractor_auto: boolean
  inspector_uuid: string
  inspector_label: string
  /** Secondary context for picked inspector (email · role). */
  inspector_secondary: string
  scheduled_for_date: string
  scheduled_for_time: string
  trigger_notes: string
  // Inline target snapshot - only required when sub-type doesn't
  // require a holding/claim AND user hasn't picked a Holding yet
  // (PJW New Qualifying fires before warranty submission so the
  // Holding may not exist yet - but the inspector still needs to
  // physically go somewhere and visit someone).
  property_address_line_1: string
  property_address_line_2: string
  property_city: string
  property_state: string
  property_postal_code: string
  property_country: string
  consumer_name: string
  consumer_email: string
  consumer_phone: string
  notify_customer: boolean
  notify_contractor: boolean
}

const EMPTY_STATE: WizardState = {
  sub_type_key: '',
  claim_uuid: '',
  claim_label: '',
  claim_secondary: '',
  holding_uuid: '',
  holding_label: '',
  holding_secondary: '',
  holding_address: '',
  warranty_uuid: '',
  contractor_uuid: '',
  contractor_label: '',
  contractor_secondary: '',
  contractor_auto: false,
  inspector_uuid: '',
  inspector_label: '',
  inspector_secondary: '',
  scheduled_for_date: '',
  scheduled_for_time: '09:00',
  trigger_notes: '',
  property_address_line_1: '',
  property_address_line_2: '',
  property_city: '',
  property_state: '',
  property_postal_code: '',
  property_country: '',
  consumer_name: '',
  consumer_email: '',
  consumer_phone: '',
  notify_customer: true,
  notify_contractor: true,
}

function StepDots({ step }: { step: Step }) {
  const stepOrder: Step[] = ['sub-type', 'context', 'review']
  return (
    <FormStepper
      steps={['Sub-type', 'Details', 'Review']}
      currentStep={stepOrder.indexOf(step)}
    />
  )
}

function SubTypeCard({
  subType,
  selected,
  onSelect,
}: {
  subType: InspectionSubType
  selected: boolean
  onSelect: () => void
}) {
  const Icon = ICONS[subType.icon] ?? ClipboardCheck
  return (
    <button
      type="button"
      onClick={onSelect}
      className={
        selected
          ? 'flex w-full flex-col gap-2 rounded-lg border-2 border-[var(--ink)] bg-[var(--bg-light)] p-5 text-left transition-colors'
          : 'flex w-full flex-col gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5 text-left transition-colors hover:border-[var(--ink-3)]'
      }
    >
      <div className="flex items-center gap-3">
        <Icon size={20} className="text-[var(--ink-2)]" />
        <span className="text-sm font-medium text-[var(--ink)]">
          {subType.label}
        </span>
      </div>
      {subType.description && (
        <p className="text-xs text-[var(--ink-3)] leading-relaxed">
          {subType.description}
        </p>
      )}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {subType.requires_claim && (
          <span className="rounded-full bg-[var(--bg-light)] px-2 py-0.5 text-[10px] text-[var(--ink-3)]">
            Requires claim
          </span>
        )}
        {subType.requires_holding && (
          <span className="rounded-full bg-[var(--bg-light)] px-2 py-0.5 text-[10px] text-[var(--ink-3)]">
            Requires holding
          </span>
        )}
        <span className="rounded-full bg-[var(--bg-light)] px-2 py-0.5 text-[10px] text-[var(--ink-3)]">
          {subType.trigger_source.replace('_', ' ')}
        </span>
      </div>
    </button>
  )
}

export function InspectionCreateWizardPage() {
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canCreate = can('inspections.create')
  const [step, setStep] = useState<Step>('sub-type')
  const [state, setState] = useState<WizardState>(EMPTY_STATE)
  const { data: subTypes, isLoading: loadingSubTypes } = useInspectionSubTypes()
  const create = useCreateInspection()

  // Searchable-picker query state - typeahead inputs for the three
  // linkage fields. Picker swaps the raw "paste UUID" pattern from
  // earlier wizards.
  const { orgUuid } = useOrg()
  const [holdingSearch, setHoldingSearch] = useState('')
  const [claimSearch, setClaimSearch] = useState('')
  const [contractorSearch, setContractorSearch] = useState('')
  const [inspectorSearch, setInspectorSearch] = useState('')
  const {
    data: holdingResults,
    isLoading: loadingHoldings,
  } = useHoldingSearch(holdingSearch)
  const {
    data: claimResults,
    isLoading: loadingClaims,
  } = useClaimSearch(claimSearch)
  const {
    data: contractorResults,
    isLoading: loadingContractors,
  } = usePartnershipSearch(orgUuid, contractorSearch)

  // Fetch quota info for every contractor in the current search result
  // set so the picker can badge "behind by N this year" inline. The
  // hook itself sorts UUIDs so the cache key is stable across keystrokes.
  const contractorOrgUuids = useMemo(
    () =>
      (contractorResults ?? [])
        .map((p) => p.downstream_organisation?.uuid)
        .filter((u): u is string => !!u),
    [contractorResults],
  )
  const { data: contractorQuotas } = useContractorQuotas(contractorOrgUuids)
  const quotaByOrgUuid = useMemo(() => {
    const map = new Map<string, (typeof contractorQuotas)[number]>()
    for (const q of contractorQuotas ?? []) {
      map.set(q.contractor_uuid, q)
    }
    return map
  }, [contractorQuotas])
  const {
    data: inspectorResults,
    isLoading: loadingInspectors,
  } = useInspectorSearch(inspectorSearch)

  const selectedSubType = useMemo(
    () => subTypes?.find((s) => s.key === state.sub_type_key),
    [subTypes, state.sub_type_key],
  )

  const update = useCallback(
    (patch: Partial<WizardState>) =>
      setState((prev) => ({ ...prev, ...patch })),
    [],
  )

  // Derive a ResolvedAddress for the AddressCapture component from
  // the flat property_* state fields, and translate its onChange back
  // into flat field updates. AddressCapture autocompletes against the
  // platform's address lookup API (Loqate / Smarty depending on the
  // country) and resolves picked suggestions to a structured address.
  const propertyAddress: ResolvedAddress | null = useMemo(() => {
    if (!state.property_address_line_1) return null
    return {
      line_1: state.property_address_line_1,
      line_2: state.property_address_line_2,
      town_or_city: state.property_city,
      county: state.property_state,
      postcode: state.property_postal_code,
      country: state.property_country,
    }
  }, [
    state.property_address_line_1,
    state.property_address_line_2,
    state.property_city,
    state.property_state,
    state.property_postal_code,
    state.property_country,
  ])
  const handlePropertyAddressChange = useCallback(
    (next: ResolvedAddress | null) => {
      if (next === null) {
        update({
          property_address_line_1: '',
          property_address_line_2: '',
          property_city: '',
          property_state: '',
          property_postal_code: '',
          property_country: '',
        })
        return
      }
      update({
        property_address_line_1: next.line_1,
        property_address_line_2: next.line_2,
        property_city: next.town_or_city,
        // ResolvedAddress uses UK-style ``county``; the Inspection
        // snapshot calls the equivalent field ``state``. The address
        // lookup returns both (US: state name; UK: county) into the
        // same string slot.
        property_state: next.county,
        property_postal_code: next.postcode,
        property_country: next.country,
      })
    },
    [update],
  )

  // True when the sub-type has neither a required Claim nor a required
  // Holding (today: PJW New Qualifying). In this case the wizard needs
  // an inline target block so the inspector has somewhere to go.
  const needsInlineTarget = useMemo(
    () =>
      !!selectedSubType &&
      !selectedSubType.requires_claim &&
      !selectedSubType.requires_holding,
    [selectedSubType],
  )

  // When the inline target is showing AND the user hasn't picked a
  // Holding, require property + customer + contractor inline.
  const inlineTargetRequired = needsInlineTarget && !state.holding_uuid

  const canAdvanceFromContext = useMemo(() => {
    if (!selectedSubType) return false
    if (selectedSubType.requires_claim && !state.claim_uuid) return false
    if (selectedSubType.requires_holding && !state.holding_uuid) return false
    if (inlineTargetRequired) {
      if (!state.property_address_line_1.trim()) return false
      if (!state.consumer_name.trim()) return false
      if (!state.contractor_uuid) return false
    }
    return true
  }, [selectedSubType, state, inlineTargetRequired])

  const handleSubmit = useCallback(async () => {
    if (!selectedSubType) return
    // Combine the user's local date + time into a Date in their local
    // timezone, then serialize to UTC ISO with explicit Z suffix so the
    // backend stores an unambiguous instant. Without this, a bare
    // datetime string ("2026-05-05T22:30:00") gets interpreted as UTC
    // server-side, then re-converted to local on read → off-by-N-hour
    // drift depending on the user's offset.
    const scheduled =
      state.scheduled_for_date && state.scheduled_for_time
        ? new Date(
            `${state.scheduled_for_date}T${state.scheduled_for_time}:00`,
          ).toISOString()
        : null

    // Strip free-text from UUID-only fields so the backend doesn't reject
    // the whole request when a tester typed a contractor's name into
    // "Contractor UUID". Surface what was stripped so the user knows.
    const sanitised = {
      claim_uuid: uuidOrNull(state.claim_uuid),
      holding_uuid: uuidOrNull(state.holding_uuid),
      warranty_uuid: uuidOrNull(state.warranty_uuid),
      contractor_uuid: uuidOrNull(state.contractor_uuid),
      inspector_uuid: uuidOrNull(state.inspector_uuid),
    }
    const strippedFieldLabels: string[] = []
    if (state.claim_uuid.trim() && !sanitised.claim_uuid)
      strippedFieldLabels.push('Claim')
    if (state.holding_uuid.trim() && !sanitised.holding_uuid)
      strippedFieldLabels.push('Holding')
    if (state.warranty_uuid.trim() && !sanitised.warranty_uuid)
      strippedFieldLabels.push('Warranty')
    if (state.contractor_uuid.trim() && !sanitised.contractor_uuid)
      strippedFieldLabels.push('Contractor')
    if (state.inspector_uuid.trim() && !sanitised.inspector_uuid)
      strippedFieldLabels.push('Inspector')

    if (strippedFieldLabels.length > 0) {
      toast.warning(
        `Ignored unrecognised input in: ${strippedFieldLabels.join(', ')}.`,
        { duration: 6000 },
      )
    }

    // Hard block: if sub-type requires a real claim or holding but the
    // user didn't paste a valid UUID, stop before hitting the backend.
    if (selectedSubType.requires_claim && !sanitised.claim_uuid) {
      toast.error(`${selectedSubType.label} requires a linked claim.`)
      return
    }
    if (selectedSubType.requires_holding && !sanitised.holding_uuid) {
      toast.error(`${selectedSubType.label} requires a linked holding.`)
      return
    }

    // Inline target snapshot - only sent when the wizard needs it
    // (sub-type doesn't require holding/claim AND no Holding picked).
    // Backend prefers Holding when present, so passing these alongside
    // a Holding is safe but pointless.
    const inlineTarget =
      !selectedSubType.requires_holding &&
      !selectedSubType.requires_claim &&
      !sanitised.holding_uuid
        ? {
            property_address_line_1: state.property_address_line_1 || undefined,
            property_address_line_2: state.property_address_line_2 || undefined,
            property_city: state.property_city || undefined,
            property_state: state.property_state || undefined,
            property_postal_code: state.property_postal_code || undefined,
            property_country: state.property_country || undefined,
            consumer_name: state.consumer_name || undefined,
            consumer_email: state.consumer_email || undefined,
            consumer_phone: state.consumer_phone || undefined,
          }
        : {}

    try {
      const inspection = await create.mutateAsync({
        sub_type_key: selectedSubType.key,
        trigger_source: 'manual',
        trigger_notes: state.trigger_notes || undefined,
        ...sanitised,
        ...inlineTarget,
        notify_customer: state.notify_customer,
        notify_contractor: state.notify_contractor,
        scheduled_for: scheduled,
      })
      toast.success(`Inspection ${inspection.code} created.`)
      navigate(`/inspections/${inspection.uuid}`)
    } catch (e) {
      toastApiError(e, 'Failed to create inspection.')
    }
  }, [create, navigate, selectedSubType, state])

  if (!canCreate) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <div className="mx-auto flex max-w-[640px] flex-col gap-3">
            <h1 className="font-display text-[42px] leading-[1.1] text-[var(--ink)]">
              New Inspection
            </h1>
            <p className="text-sm text-[var(--ink-3)]">
              You do not have permission to create inspections. Contact an
              administrator if you need access.
            </p>
            <Button
              variant="outline"
              className="self-start"
              onClick={() => navigate('/inspections')}
            >
              <ArrowLeft size={14} className="mr-1.5" />
              Back to inspections
            </Button>
          </div>
        </main>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <main className="page-container py-10">
        <div className="flex flex-col gap-8">
        <header className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => navigate('/inspections')}
            className="flex items-center gap-1.5 self-start text-xs text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <ArrowLeft size={14} />
            Back to inspections
          </button>
          <h1 className="font-display text-[42px] leading-[1.1] text-[var(--ink)]">
            New Inspection
          </h1>
          <div className="w-full pt-1">
            <StepDots step={step} />
          </div>
        </header>

        <div>

        {step === 'sub-type' && (
          <section className="flex flex-col gap-4">
            <p className="text-sm text-[var(--ink-2)]">
              What kind of inspection is this?
            </p>
            {loadingSubTypes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={20} className="animate-spin text-[var(--ink-3)]" />
              </div>
            ) : (subTypes?.length ?? 0) === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-[var(--rule)] bg-[var(--bg-card)] py-12 px-6 text-center">
                <p className="text-sm text-[var(--ink-2)]">
                  No inspection types are configured for this organisation yet.
                </p>
                <p className="text-xs text-[var(--ink-3)] max-w-[420px]">
                  Inspection types control which kinds of inspections can be
                  created here. Ask an administrator to configure them, then
                  reload this page.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(subTypes ?? []).map((subType) => (
                  <SubTypeCard
                    key={subType.key}
                    subType={subType}
                    selected={state.sub_type_key === subType.key}
                    onSelect={() => update({ sub_type_key: subType.key })}
                  />
                ))}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button
                disabled={!state.sub_type_key}
                onClick={() => setStep('context')}
              >
                Continue
                <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </section>
        )}

        {step === 'context' && selectedSubType && (
          <section className="flex flex-col gap-5">
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-[var(--ink-3)]">
                {selectedSubType.label}
              </p>
              <h2 className="mt-1 font-display text-[28px] text-[var(--ink)]">
                Linkage &amp; scheduling
              </h2>
            </div>

            <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
              <div className="min-w-0">
                <DetailCard
                  title="Holding & property"
                  icon={<Home className="h-4 w-4" />}
                  collapsible={false}
                >
                  <div className="flex flex-col gap-5 p-5">
            {selectedSubType.requires_claim && (
              <SearchablePicker<ClaimRow>
                label="Claim"
                required
                search={claimSearch}
                onSearchChange={setClaimSearch}
                results={claimResults}
                isLoading={loadingClaims}
                selectedUuid={state.claim_uuid}
                selectedLabel={state.claim_label}
                selectedSecondary={state.claim_secondary}
                onSelect={(uuid, label) => {
                  const row = claimResults?.find((c) => c.uuid === uuid)
                  update({
                    claim_uuid: uuid,
                    claim_label: label,
                    claim_secondary: row
                      ? [row.product_name, row.claim_schema_name, row.claim_state]
                          .filter(Boolean)
                          .join(' · ')
                      : '',
                  })
                }}
                onClear={() =>
                  update({ claim_uuid: '', claim_label: '', claim_secondary: '' })
                }
                getOptionUuid={(c) => c.uuid}
                getOptionPrimary={(c) =>
                  c.consumer_account_name || c.product_name || c.uuid.slice(0, 8)
                }
                getOptionSecondary={(c) => {
                  const parts = [
                    c.product_name,
                    c.claim_schema_name,
                    c.claim_state,
                  ].filter(Boolean) as string[]
                  return parts.length ? parts.join(' · ') : undefined
                }}
                placeholder="Search by customer, product, or reference…"
                helperText={`Required for ${selectedSubType.label}.`}
              />
            )}

            {(selectedSubType.requires_holding ||
              !selectedSubType.requires_claim) && (
              <div>
                  <SearchablePicker
                    label={
                      selectedSubType.requires_holding
                        ? 'Holding'
                        : 'Holding (optional)'
                    }
                    required={selectedSubType.requires_holding}
                    search={holdingSearch}
                    onSearchChange={setHoldingSearch}
                    results={holdingResults}
                    isLoading={loadingHoldings}
                    selectedUuid={state.holding_uuid}
                    selectedLabel={state.holding_label}
                    selectedSecondary={state.holding_secondary}
                    onSelect={(uuid, label) => {
                      const row = holdingResults?.find((h) => h.uuid === uuid)
                      // The registration already knows who installed it, so
                      // pre-fill the contractor rather than making the
                      // coordinator look up something we can derive. Only
                      // when they have not picked one by hand - an explicit
                      // choice is never overruled (the backend applies the
                      // same fallback-not-override rule).
                      //
                      // `contractor_auto` (not "is the field empty") is what
                      // gates this: switching from holding A to holding B must
                      // re-derive B's dealer even though the field is already
                      // populated with A's. Tracking the origin in state also
                      // survives A scrolling out of the search results, which a
                      // "compare against the previous holding" check would not.
                      const dealer = row?.partnership
                      const contractorIsAuto =
                        state.contractor_auto || !state.contractor_uuid
                      const derivedContractor = !contractorIsAuto
                        ? {}
                        : dealer?.downstream_organisation_uuid
                          ? {
                              contractor_uuid:
                                dealer.downstream_organisation_uuid,
                              contractor_label:
                                dealer.downstream_organisation_name || '',
                              contractor_secondary: dealer.membership_number
                                ? `Member #${dealer.membership_number}`
                                : '',
                              contractor_auto: true,
                            }
                          : {
                              // New holding carries no dealer - clear the
                              // auto-filled one rather than leave the previous
                              // holding's contractor stuck on this trip.
                              contractor_uuid: '',
                              contractor_label: '',
                              contractor_secondary: '',
                              contractor_auto: true,
                            }
                      update({
                        holding_uuid: uuid,
                        holding_label: label,
                        holding_address: row ? holdingLocation(row) : '',
                        holding_secondary: row
                          ? [
                              holdingConsumerName(row),
                              row.product?.cached_short_name ?? '',
                              row.warranties?.[0]?.class_name ?? '',
                              row.registration_number ||
                                formatHoldingId(row.uuid),
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : '',
                        ...derivedContractor,
                      })
                    }}
                    onClear={() =>
                      update({
                        holding_uuid: '',
                        holding_label: '',
                        holding_address: '',
                        holding_secondary: '',
                      })
                    }
                    getOptionUuid={(h) => h.uuid}
                    getOptionPrimary={(h) => {
                      const name = holdingConsumerName(h)
                      if (name) return name
                      if (h.registration_number) return h.registration_number
                      if (h.product?.cached_short_name)
                        return h.product.cached_short_name
                      return formatHoldingId(h.uuid)
                    }}
                    getOptionSecondary={(h) => {
                      const parts = [
                        h.registration_number,
                        h.product?.cached_short_name,
                        holdingLocation(h),
                      ].filter(Boolean) as string[]
                      return parts.length ? parts.join(' · ') : undefined
                    }}
                    placeholder="Search by owner, registration #, product, or address…"
                    helperText={
                      selectedSubType.requires_holding
                        ? `Required for ${selectedSubType.label}.`
                        : selectedSubType.key === 'pjw_new_qualifying'
                          ? 'New Qualifying creates the registration on demand - leave blank if no holding exists yet.'
                          : 'Optional: link this inspection to an existing holding.'
                    }
                  />
                </div>
              )}

            {state.holding_uuid && (
              <div className="flex flex-col gap-1 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-4">
                <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  Property
                </span>
                <span className="text-sm text-[var(--ink)]">
                  {state.holding_address || 'No address on file for this holding'}
                </span>
                {state.holding_secondary && (
                  <span className="text-[11px] text-[var(--ink-3)]">
                    {state.holding_secondary}
                  </span>
                )}
              </div>
            )}

            {inlineTargetRequired && (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    Property &amp; customer
                  </p>
                  <p className="mt-1 text-xs text-[var(--ink-2)]">
                    No Holding is linked, so capture the address + customer
                    directly. The inspector needs somewhere physical to go and
                    someone to look for on site.
                  </p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>
                    Property address{' '}
                    <span className="text-[var(--status-error,#991b1b)]">*</span>
                  </Label>
                  <AddressCapture
                    value={propertyAddress}
                    onChange={handlePropertyAddressChange}
                    placeholder="Start typing an address or postcode…"
                  />
                  <p className="text-[11px] text-[var(--ink-3)]">
                    Smart address search - type 3+ characters and pick from the
                    suggestions to fill the full address in one step.
                  </p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="consumer_name">
                    Customer / Owner name{' '}
                    <span className="text-[var(--status-error,#991b1b)]">*</span>
                  </Label>
                  <Input
                    id="consumer_name"
                    value={state.consumer_name}
                    onChange={(e) => update({ consumer_name: e.target.value })}
                    placeholder="Jane Smith"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="consumer_email">Customer email</Label>
                  <Input
                    id="consumer_email"
                    type="email"
                    value={state.consumer_email}
                    onChange={(e) =>
                      update({ consumer_email: e.target.value })
                    }
                    placeholder="jane@example.com"
                  />
                  <label className="flex cursor-pointer items-center gap-2 pt-1">
                    <Checkbox
                      checked={state.notify_customer}
                      onCheckedChange={(checked) =>
                        update({ notify_customer: checked === true })
                      }
                      disabled={!state.consumer_email}
                    />
                    <span className="text-[11px] text-[var(--ink-2)]">
                      Notify this customer about the inspection (scheduled,
                      completed, result letter)
                    </span>
                  </label>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Customer phone</Label>
                  <PhoneInput
                    value={state.consumer_phone}
                    onChange={(v) => update({ consumer_phone: v })}
                    hideExtension
                  />
                </div>
              </div>
            )}
                  </div>
                </DetailCard>
              </div>

              <div className="min-w-0">
                <DetailCard
                  title="Assignment & schedule"
                  icon={<CalendarCheck className="h-4 w-4" />}
                  collapsible={false}
                >
                  <div className="flex flex-col gap-5 p-5">
            <div className="flex flex-col gap-1.5">
              <SearchablePicker
                label={
                  inlineTargetRequired ? 'Contractor' : 'Contractor (optional)'
                }
                required={inlineTargetRequired}
                search={contractorSearch}
                onSearchChange={setContractorSearch}
                results={contractorResults}
                isLoading={loadingContractors}
                selectedUuid={state.contractor_uuid}
                selectedLabel={state.contractor_label}
                selectedSecondary={state.contractor_secondary}
                onSelect={(orgUuid, label) => {
                  // The picker's UUID for each row is the downstream
                  // ORGANISATION uuid (what Inspection.contractor FKs to),
                  // not the partnership row's own uuid. We look up the
                  // matching partnership row by its downstream_organisation
                  // to harvest the role + membership # for the chip's
                  // secondary line.
                  const row = contractorResults?.find(
                    (p) => p.downstream_organisation?.uuid === orgUuid,
                  )
                  const membership =
                    row?.attribute_summary?.membership_number ??
                    row?.attribute_summary?.member_id
                  const quota = quotaByOrgUuid.get(orgUuid)
                  const quotaLabel =
                    quota && quota.required_inspections > 0
                      ? quota.behind_schedule
                        ? `${quota.completed_in_year}/${quota.required_inspections} this year · behind by ${quota.inspections_remaining}`
                        : `${quota.completed_in_year}/${quota.required_inspections} this year`
                      : null
                  const secondaryParts = [
                    row?.role_name,
                    typeof membership === 'string' || typeof membership === 'number'
                      ? `#${membership}`
                      : null,
                    quotaLabel,
                  ].filter(Boolean) as string[]
                  update({
                    contractor_uuid: orgUuid,
                    contractor_label: label,
                    contractor_secondary: secondaryParts.join(' · '),
                    // An explicit pick: the holding must not overrule it.
                    contractor_auto: false,
                  })
                }}
                onClear={() =>
                  update({
                    contractor_uuid: '',
                    contractor_label: '',
                    contractor_secondary: '',
                    // A deliberate clear is explicit too - a later holding
                    // change should not silently re-fill it.
                    contractor_auto: false,
                  })
                }
                getOptionUuid={(p) =>
                  // Send the ORG uuid, not the partnership row uuid -
                  // Inspection.contractor FKs to Organisation, not
                  // Partnership. (Was: "Contractor not found for this
                  // tenant." error when the partnership uuid was sent.)
                  p.downstream_organisation?.uuid || p.uuid
                }
                getOptionPrimary={(p) =>
                  p.downstream_organisation?.name || p.uuid.slice(0, 8)
                }
                getOptionSecondary={(p) => {
                  const orgUuid = p.downstream_organisation?.uuid
                  const quota = orgUuid
                    ? quotaByOrgUuid.get(orgUuid)
                    : undefined
                  const parts: string[] = []
                  if (p.role_name) parts.push(p.role_name)
                  if (quota && quota.required_inspections > 0) {
                    const label = quota.behind_schedule
                      ? `${quota.completed_in_year}/${quota.required_inspections} this year · behind by ${quota.inspections_remaining}`
                      : `${quota.completed_in_year}/${quota.required_inspections} this year`
                    parts.push(label)
                  }
                  return parts.length ? parts.join(' · ') : undefined
                }}
                placeholder="Search contractor name…"
                helperText="Optional: link the contractor whose work is being inspected. Quota badges show this year's inspection progress."
              />
              <label className="flex cursor-pointer items-center gap-2 pt-1">
                <Checkbox
                  checked={state.notify_contractor}
                  onCheckedChange={(checked) =>
                    update({ notify_contractor: checked === true })
                  }
                  disabled={!state.contractor_uuid}
                />
                <span className="text-[11px] text-[var(--ink-2)]">
                  Notify this contractor about the inspection (scheduled,
                  repair tracking, result)
                </span>
              </label>
            </div>

            <SearchablePicker
              label="Inspector (FTS, optional)"
              search={inspectorSearch}
              onSearchChange={setInspectorSearch}
              results={inspectorResults}
              isLoading={loadingInspectors}
              selectedUuid={state.inspector_uuid}
              selectedLabel={state.inspector_label}
              selectedSecondary={state.inspector_secondary}
              onSelect={(uuid, label) => {
                const row = inspectorResults?.find((u) => u.uuid === uuid)
                const secondary =
                  row && row.email !== row.full_name
                    ? `${row.email}${row.role ? ` · ${row.role}` : ''}`
                    : row?.role || ''
                update({
                  inspector_uuid: uuid,
                  inspector_label: label,
                  inspector_secondary: secondary,
                })
              }}
              onClear={() =>
                update({
                  inspector_uuid: '',
                  inspector_label: '',
                  inspector_secondary: '',
                })
              }
              getOptionUuid={(u) => u.uuid}
              getOptionPrimary={(u) => u.full_name || u.email}
              getOptionSecondary={(u) =>
                u.email !== u.full_name
                  ? `${u.email}${u.role ? ` · ${u.role}` : ''}`
                  : u.role || undefined
              }
              placeholder="Search inspector by name or email…"
              helperText="Optional: assign now, or via the Scheduling Console."
            />

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Schedule (optional)</Label>
                <Link
                  to="/inspections/scheduling"
                  className="text-[11px] text-[var(--ink-3)] underline-offset-2 hover:text-[var(--ink)] hover:underline"
                >
                  Open Scheduling Console →
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
                <DatePicker
                  value={state.scheduled_for_date}
                  onChange={(v) => update({ scheduled_for_date: v })}
                  placeholder="Leave blank if you don't know yet"
                  disablePast
                />
                <TimeSelect
                  value={state.scheduled_for_time}
                  onChange={(v) => update({ scheduled_for_time: v })}
                  ariaLabel="Scheduled time"
                  className="sm:w-[160px]"
                />
              </div>
              <p className="text-[11px] text-[var(--ink-3)]">
                Not sure when yet? Leave both blank - the inspection lands in{' '}
                <strong>draft</strong> and the Coordinator schedules it from the{' '}
                <strong>Scheduling Console</strong>, where the inspector's
                week-view shows existing route load.
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="trigger_notes">Notes (optional)</Label>
              <textarea
                id="trigger_notes"
                value={state.trigger_notes}
                onChange={(e) => update({ trigger_notes: e.target.value })}
                placeholder="Anything to remember about this inspection..."
                className="min-h-[80px] rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-3 text-sm text-[var(--ink)] focus:border-[var(--ink-3)] focus:outline-none"
              />
            </div>
                  </div>
                </DetailCard>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep('sub-type')}
              >
                <ArrowLeft size={14} className="mr-1.5" />
                Back
              </Button>
              <Button
                disabled={!canAdvanceFromContext}
                onClick={() => setStep('review')}
              >
                Continue
                <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </section>
        )}

        {step === 'review' && selectedSubType && (
          <section className="flex flex-col gap-5">
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Review &amp; create
              </p>
              <h2 className="mt-1 font-display text-[28px] text-[var(--ink)]">
                Confirm the inspection
              </h2>
            </div>

            <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
              <div className="min-w-0">
                <DetailCard
                  title="Holding & property"
                  icon={<Home className="h-4 w-4" />}
                  collapsible={false}
                  trailing={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => setStep('context')}
                      title="Edit details"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  }
                >
                  <div className="flex flex-col gap-3 p-5">
                    <Field label="Sub-type" value={selectedSubType.label} />
                    {state.claim_uuid && (
                      <Field label="Claim" value={state.claim_uuid} mono />
                    )}
                    {state.holding_uuid && (
                      <Field
                        label="Holding"
                        value={state.holding_label || state.holding_uuid}
                        secondary={state.holding_secondary || undefined}
                      />
                    )}
                    {state.holding_uuid && state.holding_address && (
                      <Field label="Property" value={state.holding_address} />
                    )}
                    {!state.holding_uuid && state.property_address_line_1 && (
                      <Field
                        label="Property"
                        value={state.property_address_line_1}
                        secondary={[
                          state.property_address_line_2,
                          [
                            state.property_city,
                            state.property_state,
                            state.property_postal_code,
                          ]
                            .filter(Boolean)
                            .join(', '),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    )}
                    {!state.holding_uuid && state.consumer_name && (
                      <Field
                        label="Customer"
                        value={state.consumer_name}
                        secondary={[state.consumer_email, state.consumer_phone]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    )}
                  </div>
                </DetailCard>
              </div>

              <div className="min-w-0">
                <DetailCard
                  title="Assignment & schedule"
                  icon={<CalendarCheck className="h-4 w-4" />}
                  collapsible={false}
                  trailing={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => setStep('context')}
                      title="Edit details"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  }
                >
                  <div className="flex flex-col gap-3 p-5">
                    {state.contractor_uuid && (
                      <Field
                        label="Contractor"
                        value={state.contractor_label || state.contractor_uuid}
                        secondary={state.contractor_secondary || undefined}
                      />
                    )}
                    {state.inspector_uuid && (
                      <Field
                        label="Inspector"
                        value={state.inspector_label || state.inspector_uuid}
                        secondary={state.inspector_secondary || undefined}
                      />
                    )}
                    {state.scheduled_for_date && (
                      <Field
                        label="Scheduled date"
                        value={state.scheduled_for_date}
                      />
                    )}
                    <Field label="Trigger source" value="Manual" />
                    {state.trigger_notes && (
                      <Field label="Notes" value={state.trigger_notes} />
                    )}
                  </div>
                </DetailCard>
              </div>
            </div>

            <p className="text-xs text-[var(--ink-3)]">
              Lands in <strong>draft</strong> status. The detail page is where
              you complete scheduling, capture the risk assessment, run the
              inspection, and log results.
            </p>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep('context')}
                disabled={create.isPending}
              >
                <ArrowLeft size={14} className="mr-1.5" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={create.isPending}
              >
                {create.isPending ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Inspection'
                )}
              </Button>
            </div>
          </section>
        )}
        </div>
        </div>
      </main>
    </AppShell>
  )
}

function Field({
  label,
  value,
  secondary,
  mono,
  className,
}: {
  label: string
  value: string
  /** Optional second line - used to show the UUID under a human label. */
  secondary?: string
  mono?: boolean
  className?: string
}) {
  return (
    <div className={`flex flex-col gap-0.5 ${className ?? ''}`}>
      <span className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
      </span>
      <span
        className={
          mono
            ? 'font-mono text-[12px] text-[var(--ink)]'
            : 'text-sm text-[var(--ink)]'
        }
      >
        {value}
      </span>
      {secondary && (
        <span className="text-[11px] text-[var(--ink-3)]">{secondary}</span>
      )}
    </div>
  )
}
